import {
  AppendAssignedAreasOperationRequest,
  BookingsApi,
  Configuration,
  ReplaceAssignedAreasRequest,
  ViewBooking,
  ViewBookingAssignedAreasInner,
  ViewBookingCommentsInnerToJSON
} from "collins-js-sdk";
import { DiaryContextType } from "../../context/DiaryContext";
import { BookingV4 } from "../../types/bookings";
import { updateStateBooking } from "../../utils/booking";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import {
  convertAllDatesToUTC,
  convertObjectCamelCaseToSnakeCase,
  convertObjectSnakeCaseToCamelCase,
  getDefinedProps
} from "../../utils/helpers";
import { updateBookingCacheStorage } from "../../utils/localStorage/apiCacheStorage";
import { apiFetchNotOkHandler, errorHandler } from "../errorHandler";

/**
 * Add or update the booking
 * @param booking
 */
export type editBooking = {
  unsaved?: boolean;
  deleted?: boolean;
  additionalAssignedAreas?: string[] | null;
};
type updateBookingType = {
  booking: ViewBooking & editBooking;
  diaryContext?: DiaryContextType;
};
export const addOrUpdateBooking = async ({
  booking,
  diaryContext
}: updateBookingType) => {
  booking.lastUpdated = new Date();
  const bookingResponse = await saveBooking({ ...booking });

  if (!diaryContext) {
    return bookingResponse;
  }
  if (bookingResponse) {
    updateStateBooking(bookingResponse, diaryContext);
  }

  if (bookingResponse?.unsaved) {
    updateBookingCacheStorage({
      booking: bookingResponse,
      venueId: diaryContext.venueConfig?._id
    });
  }

  return bookingResponse;
};

export const saveBooking = async (
  booking: ViewBooking & editBooking
): Promise<ViewBooking & editBooking> => {
  let unsaved = true;
  const newBooking = !booking.id;
  let updatedBookingResponse: (ViewBooking & editBooking) | undefined =
    undefined;

  if (newBooking) {
    // update array directly, resolves excessive re-rendering and changes flickering
    updatedBookingResponse = await addBooking(booking);
  }

  if (!newBooking) {
    updatedBookingResponse = await postUpdateBooking(booking);
  }

  if (updatedBookingResponse?.id) {
    unsaved = false;
  }

  return {
    ...booking,
    ...getDefinedProps(updatedBookingResponse ?? booking),
    unsaved
  };
};

export const addBooking = async (
  booking: ViewBooking
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings`;
  let response;

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(convertAllDatesToUTC(mapBookingToV4(booking)))
    });
  } catch (error) {
    errorHandler(error, "Error adding booking");
    return undefined;
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, "Error adding booking");
    return undefined;
  }

  const newBooking = await response.json();

  const viewBooking: ViewBooking = mapBookingV4ToState(
    newBooking.payload.booking
  );
  viewBooking.id = newBooking.payload.booking._id;

  const areas: Array<string> = [];
  if (booking.assignedAreas) {
    booking.assignedAreas.forEach((ViewBookingAssignedAreasInner) => {
      const areaId = `${ViewBookingAssignedAreasInner.id}`;
      areas.push(areaId);
    });
  }

  // add booking doesn't set the booking area, so grab the new id and call assignBookingArea
  if (
    viewBooking.id &&
    booking.assignedAreas &&
    booking.assignedAreas.length > 0
  ) {
    const newBookingAssignedAreas = await assignBookingArea({
      bookingId: viewBooking.id,
      appendAssignedAreasRequest: { areas }
    });

    if (newBookingAssignedAreas?.assignedAreas) {
      viewBooking.assignedAreas =
        newBookingAssignedAreas.assignedAreas as ViewBookingAssignedAreasInner[];
    }
  }
  return viewBooking;
};

const postUpdateBooking = async (
  booking: ViewBooking
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${booking.id}`;
  let response;

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(mapBookingToV4(booking))
    });
  } catch {
    return undefined;
  }

  if (!response?.ok) {
    return undefined;
  }

  const newBooking = await response.json();

  const viewBooking: ViewBooking = mapBookingV4ToState(
    newBooking.payload.booking
  );

  return viewBooking;
};

export const postUpdateBookingV4 = async (
  booking: BookingV4
): Promise<BookingV4 | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${booking._id}`;
  let response;

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(booking)
    });
  } catch {
    return undefined;
  }

  if (!response?.ok) {
    return undefined;
  }

  const newBooking = await response.json();

  return newBooking.payload.booking;
};

/**
 * Append the area to the booking
 * @returns
 */
const assignBookingArea = async (
  params: AppendAssignedAreasOperationRequest
) => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const config = new Configuration({
    accessToken: token,
    basePath: environmentVars?.COLLINS_API_BASE_URL
  });
  try {
    return await new BookingsApi(config).appendAssignedAreas(params);
  } catch {
    return undefined;
  }
};

export const replaceAssignedAreas = async (
  params: ReplaceAssignedAreasRequest
) => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const config = new Configuration({
    accessToken: token,
    basePath: environmentVars?.COLLINS_API_BASE_URL
  });
  try {
    return await new BookingsApi(config).replaceAssignedAreas(params);
  } catch {
    return undefined;
  }
};

/**
 * Delete a booking (soft-delete: sets status to 'deleted')
 * @param bookingId - The ID of the booking to delete
 * @param reason - Optional reason for deletion: 'spam', 'duplicate', or 'other'
 * @returns true if the deletion was successful, false otherwise
 */
export const deleteBooking = async (
  bookingId: string,
  reason: string = "other"
): Promise<boolean> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${bookingId}`;

  try {
    const response = await fetch(
      `${bookingUrl}?reason=${encodeURIComponent(reason)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response?.ok) {
      await apiFetchNotOkHandler(response, "Error deleting booking");
      return false;
    }

    return true;
  } catch (error) {
    errorHandler(error, "Error deleting booking");
    return false;
  }
};

/**
 * Restore a previously deleted booking
 * @param bookingId - The ID of the booking to restore
 * @returns The restored booking, or undefined on failure
 */
export const restoreBooking = async (
  bookingId: string
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${bookingId}/restore`;

  try {
    const response = await fetch(bookingUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response?.ok) {
      await apiFetchNotOkHandler(response, "Error restoring booking");
      return undefined;
    }

    const data = await response.json();
    return mapBookingV4ToState(data.payload.booking);
  } catch (error) {
    errorHandler(error, "Error restoring booking");
    return undefined;
  }
};

export const mapBookingToV4 = (booking: ViewBooking): BookingV4 => {
  const bookingV4 = convertObjectCamelCaseToSnakeCase(
    booking
  ) as unknown as BookingV4;

  return {
    ...bookingV4,
    _id: booking.id ?? bookingV4._id,
    assigned_area: bookingV4.assigned_areas?.[0]
      ? {
          id: bookingV4.assigned_areas?.[0]?.id?.toString() ?? "",
          name: bookingV4.assigned_areas?.[0]?.name?.toString() ?? ""
        }
      : null,
    assignMode: false,
    managed_by_venue: true,
    source: "admin",
    comments:
      booking.comments?.map((comment) =>
        ViewBookingCommentsInnerToJSON(comment)
      ) ?? [],
    waitlist_details: {
      ...bookingV4.waitlist_details,
      availableTime: bookingV4.waitlist_details?.available_time
    }
  } as unknown as BookingV4;
};

export const mapBookingV4ToState = (booking: Partial<BookingV4>): ViewBooking =>
  ({
    id: booking._id,
    actualGuests: booking.actual_guests ?? booking.num_people,
    reference: booking.booking_id,
    customerId: booking.user_id,
    currentStage: booking.current_stage ?? "",
    waitlistDetails: {
      ...booking.waitlist_details,
      availableTime: booking.waitlist_details?.available_time
    },
    ...convertObjectSnakeCaseToCamelCase(booking),
    tags: booking.tags
  }) as ViewBooking;
